import { useRef, useState } from "react";
import { toast } from "sonner";
import { Info, Upload, X } from "lucide-react";
import SectionCard, { inputCls, labelCls } from "./SectionCard";
import { api, photoUrl, uploadFiles, formatApiError } from "../../api";
import { fmtKm, trUpper, vehicleDeleteRemainingMs, fmtHourMin, VEHICLE_DELETE_NOTE } from "../../lib/labels";
import { BodyTypeField, TrimField, BrandField, ModelField, YearField, EngineField, FuelField, TransmissionField, ColorField } from "../VehicleSpecFields";

function InfoView({ vehicle }) {
  const specs = [
    ["Şasi No / VIN", vehicle.vin], ["Marka", vehicle.brand], ["Model", vehicle.model],
    ["Araç Paketi", vehicle.trim || "Belirtilmedi"], ["Kasa Tipi", vehicle.body_type || "Belirtilmedi"],
    ["Model Yılı", vehicle.year], ["Plaka", vehicle.plate || "Belirtilmedi"],
    ["Kilometre", fmtKm(vehicle.km)], ["Motor", vehicle.engine || "Belirtilmedi"],
    ["Yakıt Tipi", vehicle.fuel || "Belirtilmedi"], ["Vites", vehicle.transmission || "Belirtilmedi"],
    ["Renk", vehicle.color || "Belirtilmedi"],
  ];
  const delRemain = vehicleDeleteRemainingMs(vehicle.created_at);
  return (
    <div>
      {vehicle.photos && vehicle.photos.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2 mb-5">
          {vehicle.photos.map((p) => (
            <img key={p} src={photoUrl(p)} alt="" className="h-28 w-40 object-cover rounded-xl border border-white/10 shrink-0" />
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
        {specs.map(([l, v]) => (
          <div key={l}>
            <p className="text-xs text-slate-500">{l}</p>
            <p className="mt-0.5 font-semibold text-slate-100 text-sm break-words">{v}</p>
          </div>
        ))}
      </div>
      <div className="mt-5 rounded-xl border border-white/10 bg-slate-950/50 px-4 py-3">
        <p data-testid="vehicle-delete-note" className="text-xs text-slate-500">{VEHICLE_DELETE_NOTE}</p>
        {delRemain !== null && (
          <p data-testid="vehicle-delete-remaining" className={`text-xs mt-1 font-semibold ${delRemain > 0 ? "text-amber-300" : "text-red-300"}`}>
            {delRemain > 0 ? `Silme süresi: ${fmtHourMin(delRemain)} kaldı` : "Silme süresi doldu — bu kaydı yalnızca yönetici yönetebilir"}
          </p>
        )}
      </div>
    </div>
  );
}

function InfoForm({ form, setForm, uploading, fileRef, onPickFiles, onRemovePhoto }) {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setU = (k) => (e) => setForm((f) => ({ ...f, [k]: trUpper(e.target.value) }));
  return (
    <div className="space-y-4">
      <div>
        <label className={labelCls}>Şasi No / VIN</label>
        <input data-testid="bilgiler-vin" disabled value={form.vin} className={`${inputCls} font-mono-vin tracking-widest opacity-50`} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <BrandField value={form.brand} testid="bilgiler-brand" inputCls={inputCls} labelCls={labelCls}
          onChange={(v) => setForm((f) => ({ ...f, brand: v, model: "", trim: "", engine: "" }))} />
        <ModelField brand={form.brand} value={form.model} testid="bilgiler-model" inputCls={inputCls} labelCls={labelCls}
          onChange={(v) => setForm((f) => ({ ...f, model: v, engine: "" }))} />
        <YearField value={form.year} testid="bilgiler-year" inputCls={inputCls} labelCls={labelCls}
          onChange={(v) => setForm((f) => ({ ...f, year: v }))} />
        <div><label className={labelCls}>Plaka</label>
          <input data-testid="bilgiler-plate" value={form.plate} onChange={setU("plate")} className={inputCls} /></div>
        <div><label className={labelCls}>Kilometre</label>
          <input data-testid="bilgiler-km" type="number" min="0" value={form.km} onChange={set("km")} className={inputCls} /></div>
        <EngineField brand={form.brand} value={form.engine} testid="bilgiler-engine" inputCls={inputCls} labelCls={labelCls}
          onChange={(v) => setForm((f) => ({ ...f, engine: v }))} />
        <FuelField value={form.fuel} testid="bilgiler-fuel" inputCls={inputCls} labelCls={labelCls}
          onChange={(v) => setForm((f) => ({ ...f, fuel: v }))} />
        <TransmissionField value={form.transmission} testid="bilgiler-transmission" inputCls={inputCls} labelCls={labelCls}
          onChange={(v) => setForm((f) => ({ ...f, transmission: v }))} />
        <ColorField value={form.color} testid="bilgiler-color" inputCls={inputCls} labelCls={labelCls}
          onChange={(v) => setForm((f) => ({ ...f, color: v }))} />
        <BodyTypeField value={form.body_type} onChange={(v) => setForm((f) => ({ ...f, body_type: v }))}
          testid="bilgiler-body-type" inputCls={inputCls} labelCls={labelCls} />
        <TrimField brand={form.brand} value={form.trim} onChange={(v) => setForm((f) => ({ ...f, trim: v }))}
          testid="bilgiler-trim" inputCls={inputCls} labelCls={labelCls} />
      </div>
      <div>
        <label className={labelCls}>Araç Fotoğrafları</label>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
          onChange={(e) => { onPickFiles([...e.target.files]); e.target.value = ""; }} />
        <button type="button" data-testid="bilgiler-upload-button" onClick={() => fileRef.current?.click()} disabled={uploading}
          className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl border border-dashed border-white/20 text-slate-300 text-sm hover:border-cyan-400/50 hover:text-cyan-300 transition-colors disabled:opacity-50">
          <Upload className="w-4 h-4" /> {uploading ? "Yükleniyor..." : "Fotoğraf Yükle"}
        </button>
        {form.photos.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {form.photos.map((p, i) => (
              <div key={p} className="relative">
                <img src={photoUrl(p)} alt="" className="w-20 h-14 object-cover rounded-lg border border-white/10" />
                <button type="button" data-testid={`bilgiler-remove-photo-${i}`} onClick={() => onRemovePhoto(i)}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function InfoSection({ vehicle, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(null);
  const fileRef = useRef(null);

  const startEdit = () => {
    setForm({
      vin: vehicle.vin, plate: vehicle.plate || "", brand: vehicle.brand, model: vehicle.model,
      year: vehicle.year, km: vehicle.km || 0, engine: vehicle.engine || "", fuel: vehicle.fuel || "",
      transmission: vehicle.transmission || "", color: vehicle.color || "",
      body_type: vehicle.body_type || "", trim: vehicle.trim || "", photos: vehicle.photos || [],
    });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/my-vehicles/${vehicle.id}`, { ...form, year: Number(form.year), km: Number(form.km) || 0 });
      onSaved(data);
      setEditing(false);
      toast.success("Araç bilgileri kaydedildi");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handlePickFiles = async (files) => {
    setUploading(true);
    try {
      const paths = await uploadFiles(files);
      setForm((f) => ({ ...f, photos: [...f.photos, ...paths] }));
    } catch (err) { toast.error(formatApiError(err)); }
    setUploading(false);
  };

  const removePhoto = (i) => setForm((f) => ({ ...f, photos: f.photos.filter((_, j) => j !== i) }));

  return (
    <SectionCard testid="section-bilgiler" title="Araç Bilgileri" Icon={Info}
      editing={editing} onEdit={startEdit} onCancel={() => setEditing(false)} onSave={save} saving={saving}>
      {!editing ? (
        <InfoView vehicle={vehicle} />
      ) : (
        <InfoForm form={form} setForm={setForm} uploading={uploading} fileRef={fileRef}
          onPickFiles={handlePickFiles} onRemovePhoto={removePhoto} />
      )}
    </SectionCard>
  );
}
